/**
 * ThemeDrawer — 10th drawer panel (2026-06-07).
 *
 * Lets the player pick from 6 themes (Dossier Noir default + Cold Steel +
 * Casino Royale + Bonded Brief + UK Monarchy + US/UK Flag). Each row shows
 * a 4-swatch preview (bg / accent / danger / success) + the theme's flavor
 * description.
 *
 * Selection persists in localStorage (`br-theme-v1`) via useThemeSelection.
 * The theme is realized as a class on the BriefingRoomShell root, so
 * switching it re-themes every component in O(repaint) with no re-mount.
 *
 * Wired into ScenarioRunner's drawer system alongside the existing 9 drawers
 * (stats / history / roll / audio / atmosphere / voice-log / npcs /
 * pdf-library / modules-library).
 */
import './ThemeDrawer.css';
import type {
  ThemeDescriptor,
  ThemeId,
  UseThemeSelectionResult,
} from '../../hooks/useThemeSelection';

export interface ThemeDrawerProps {
  /** Selected theme id (current). */
  theme: ThemeId;
  /** Setter from useThemeSelection (persists to localStorage). */
  setTheme: UseThemeSelectionResult['setTheme'];
  /** Theme catalog from useThemeSelection.themes. */
  themes: ReadonlyArray<ThemeDescriptor>;
  /** Testid prefix override (defaults to `br-scenario-drawer-theme`). */
  testidPrefix?: string;
}

export function ThemeDrawer({
  theme,
  setTheme,
  themes,
  testidPrefix = 'br-scenario-drawer-theme',
}: ThemeDrawerProps) {
  return (
    <aside
      className="br-scenario-drawer br-theme-drawer"
      data-testid={testidPrefix}
      aria-label="Theme chooser"
    >
      <header>
        <h2 className="br-display">Theme</h2>
        <p className="br-label br-theme-drawer-hint">
          Pick the visual palette for the briefing room. Persists across reloads.
        </p>
      </header>

      <ul
        className="br-theme-drawer-list"
        role="radiogroup"
        aria-label="Available themes"
        data-testid={`${testidPrefix}-list`}
      >
        {themes.map((t) => {
          const isActive = t.id === theme;
          return (
            <li
              key={t.id}
              className={
                isActive
                  ? 'br-theme-drawer-row br-theme-drawer-row-active'
                  : 'br-theme-drawer-row'
              }
              data-testid={`${testidPrefix}-row-${t.id}`}
              data-active={isActive}
            >
              <button
                type="button"
                role="radio"
                className="br-theme-drawer-button"
                aria-checked={isActive}
                aria-label={`Switch to ${t.label} theme`}
                data-testid={`${testidPrefix}-button-${t.id}`}
                onClick={() => setTheme(t.id)}
              >
                <span
                  className="br-theme-drawer-swatch-stack"
                  aria-hidden="true"
                  data-testid={`${testidPrefix}-swatch-${t.id}`}
                >
                  <span
                    className="br-theme-drawer-swatch br-theme-drawer-swatch-bg"
                    style={{ backgroundColor: t.swatch.bg }}
                  />
                  <span
                    className="br-theme-drawer-swatch br-theme-drawer-swatch-accent"
                    style={{ backgroundColor: t.swatch.accent }}
                  />
                  <span
                    className="br-theme-drawer-swatch br-theme-drawer-swatch-success"
                    style={{ backgroundColor: t.swatch.success }}
                  />
                  <span
                    className="br-theme-drawer-swatch br-theme-drawer-swatch-danger"
                    style={{ backgroundColor: t.swatch.danger }}
                  />
                </span>
                <span className="br-theme-drawer-meta">
                  <span className="br-theme-drawer-label br-label">{t.label}</span>
                  <span className="br-theme-drawer-description">
                    {t.description}
                  </span>
                </span>
                {isActive && (
                  <span
                    className="br-theme-drawer-active-badge br-label"
                    aria-hidden="true"
                  >
                    ACTIVE
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export default ThemeDrawer;
